/**
 * LeadTimeCalculator Web Component
 * Rational Futurism Design
 */

const HOLIDAYS = [
    // 2024
    "2024-12-30", "2024-12-31",
    // 2025
    "2025-01-01", "2025-01-02", "2025-01-03", "2025-01-13", "2025-02-11", "2025-02-23", "2025-02-24",
    "2025-03-20", "2025-04-29", "2025-05-03", "2025-05-04", "2025-05-05", "2025-05-06",
    "2025-07-21", "2025-08-11", "2025-09-15", "2025-09-23", "2025-10-13", "2025-11-03", "2025-11-23", "2025-11-24",
    "2025-12-29", "2025-12-30", "2025-12-31",
    // 2026
    "2026-01-01", "2026-01-02", "2026-01-03", "2026-01-04", "2026-01-12",
    "2026-02-11", "2026-02-23", "2026-03-20", "2026-04-29", "2026-05-03", "2026-05-04", "2026-05-05", "2026-05-06",
    "2026-07-20", "2026-08-11", "2026-09-21", "2026-09-23", "2026-10-12", "2026-11-03", "2026-11-23",
    // 2027
    "2027-01-01", "2027-01-11", "2027-02-11", "2027-02-23", "2027-03-21", "2027-03-22",
    "2027-04-29", "2027-05-03", "2027-05-04", "2027-05-05", "2027-07-19", "2027-08-11",
    "2027-09-20", "2027-09-23", "2027-10-11", "2027-11-03", "2027-11-23",
    // 2028
    "2028-01-01", "2028-01-10", "2028-02-11", "2028-02-23", "2028-03-20",
    "2028-04-29", "2028-05-03", "2028-05-04", "2028-05-05", "2028-07-17", "2028-08-11",
    "2028-09-18", "2028-09-22", "2028-10-09", "2028-11-03", "2028-11-23",
    // 2029
    "2029-01-01", "2029-01-08", "2029-02-11", "2029-02-12", "2029-02-23", "2029-03-20",
    "2029-04-29", "2029-04-30", "2029-05-03", "2029-05-04", "2029-05-05", "2029-07-16",
    "2029-08-11", "2029-09-17", "2029-09-23", "2029-09-24", "2029-10-08", "2029-11-03", "2029-11-23",
    // 2030
    "2030-01-01", "2030-01-14", "2030-02-11", "2030-02-23", "2030-03-20",
    "2030-04-29", "2030-05-03", "2030-05-04", "2030-05-05", "2030-07-15", "2030-08-11", "2030-08-12",
    "2030-09-16", "2030-09-23", "2030-10-14", "2030-11-03", "2030-11-04", "2030-11-23"
];

class LeadTimeCalculator extends HTMLElement {
    constructor() {
        super();
        this._variantMap = {};
        this._daysMap = {};
        this._currentVariantDays = 0;
        this._dailyCapacity = 10;
        this._processingPoint = 0;
        this._tags = [];
        // Tier Config
        this._limitT1 = 40;
        this._capT1 = 10;
        this._capT2 = 20;
        this._consultLimit = 220;
    }

    connectedCallback() {
        // Read data attributes
        try {
            this._variantMap = JSON.parse(this.dataset.pointsMap || '{}');
            this._daysMap = JSON.parse(this.dataset.daysMap || '{}');
            this._tags = JSON.parse(this.dataset.tags || '[]');

            // Tier parsing
            this._limitT1 = parseInt(this.dataset.limitT1 || '40', 10);
            this._capT1 = parseInt(this.dataset.capT1 || '10', 10);
            this._capT2 = parseInt(this.dataset.capT2 || '20', 10);
            this._consultLimit = parseInt(this.dataset.consultLimit || '220', 10);

            // Fetch Step overrides from block settings
            this._step2DaysOverride = this.dataset.step2Days ? parseInt(this.dataset.step2Days, 10) : 3;
            this._step3MinOverride = this.dataset.step3Min ? parseInt(this.dataset.step3Min, 10) : 1;
            this._step3MaxOverride = this.dataset.step3Max ? parseInt(this.dataset.step3Max, 10) : 4;

            // Design Step days range (from Block Settings)
            this._designDaysMin = this.dataset.designDaysMin ? parseInt(this.dataset.designDaysMin, 10) : 1;
            this._designDaysMax = this.dataset.designDaysMax ? parseInt(this.dataset.designDaysMax, 10) : 3;

            // Fallback daily capacity (legacy)
            this._dailyCapacity = parseInt(this.dataset.capacity || '10', 10);
        } catch (e) {
            console.error('LeadTimeCalculator: Invalid JSON in data attributes', e);
        }

        this._processingPoint = parseFloat(this.dataset.processingPoint || '0');

        // Init Elements
        this.els = {
            days: this.querySelector('[data-target="days"]'),
            date: this.querySelector('[data-target="date"]'),
            prefix: this.querySelector('#rf-shipping-prefix'),
            // RF Delivery Estimation Elements
            rfCountdownText: this.querySelector('#rf-countdown-text'),
            rfDateReception: this.querySelector('#rf-date-reception'),
            rfDateDesign: this.querySelector('#rf-date-design'),
            rfDateShipping: this.querySelector('#rf-date-shipping'),
            rfDateDelivery: this.querySelector('#rf-date-delivery'),
            rfStepDesign: this.querySelector('#rf-step-design'),
            rfStepShipping: this.querySelector('#rf-step-shipping'),
            rfStepDelivery: this.querySelector('#rf-step-delivery'),
            rfTextSettings: this.querySelector('#rf-delivery-text-settings')
        };

        // Settings parsing from hidden block
        this._rfSettings = {
            timerPrefix: 'あと',
            timerSuffix: '以内のご注文で「最短発送」'
        };
        if (this.els.rfTextSettings) {
            const tempPrefix = this.els.rfTextSettings.querySelector('[data-timer-prefix]');
            const tempSuffix = this.els.rfTextSettings.querySelector('[data-timer-suffix]');
            if (tempPrefix) this._rfSettings.timerPrefix = tempPrefix.innerText;
            if (tempSuffix) this._rfSettings.timerSuffix = tempSuffix.innerText;
        }

        // Listeners
        this.initListeners();

        // Initial Calc
        if (this.dataset.overridePoints) {
            // Cart Mode or Static Mode
            const points = parseFloat(this.dataset.overridePoints);
            this.updateUI(points);
        } else {
            // Product Page Mode
            this.handleProductPageUpdate();
        }
    }

    initListeners() {
        // If we have override points, we might not need to listen to form changes unless we want to handle cart updates.
        // But for cart drawer, the section rerenders on update, so connectedCallback runs again.
        if (this.dataset.overridePoints) return;

        // Product Page Listeners
        const handleChange = (e) => {
            if (e.target.matches('[name="id"], [name="quantity"], #rf-size-select, select[name="properties[サイズ]"], input[name="properties[レイアウト確認]"]')) {
                this.handleProductPageUpdate();
            }
        };

        document.addEventListener('change', handleChange);
        document.addEventListener('input', handleChange);

        // Listen for clicks on quantity buttons (delegate)
        document.addEventListener('click', (e) => {
            if (e.target.closest('.quantity-selector__button') || e.target.closest('.rf-qty-btn')) {
                // Wait for the input value to update (short delay)
                setTimeout(() => this.handleProductPageUpdate(), 50);
            }
        });

        // Or custom events from main-product.js if it fires them
        document.addEventListener('variant:changed', () => this.handleProductPageUpdate());

        // Cart Context (if integrated in cart)
        // If re-rendered via Section Rendering API, connectedCallback runs again.
        // If we want to listen to cart events for dynamic updates without re-render:
        document.addEventListener('cart:updated', (e) => {
            // If we are in the cart drawer and relying on liquid re-render, this might be redundant, 
            // but if we need to re-fetch, we'd do it here. 
            // For now, assuming section re-render handles usage.
        });
    }

    isBusinessDay(date) {
        const day = date.getDay();
        if (day === 0) return false; // Sunday
        // If IS_COUNT_SATURDAY is needed, it would be passed via dataset. For now assume Saturday is off unless configured differently.
        if (day === 6) return false;

        const y = date.getFullYear();
        const m = String(date.getMonth() + 1).padStart(2, '0');
        const d = String(date.getDate()).padStart(2, '0');
        const ds = `${y}-${m}-${d}`;

        if (HOLIDAYS.includes(ds)) return false;

        return true;
    }

    addBusinessDays(startDate, daysToAdd) {
        // [FIX] User Request: Count reception date (startDate) as Day 1.
        // Therefore, if daysToAdd is 3, we only need to add 2 more business days.
        let count = 1;
        let current = new Date(startDate);

        while (count < daysToAdd) {
            current.setDate(current.getDate() + 1);
            if (this.isBusinessDay(current)) {
                count++;
            }
        }
        return current;
    }

    getFormattedDate(date) {
        const result = `${date.getMonth() + 1}/${date.getDate()}`;
        const days = ['日', '月', '火', '水', '木', '金', '土'];
        const dayStr = days[date.getDay()];
        return `${result} (${dayStr})`;
    }

    getReceptionDate() {
        const now = new Date();
        let baseDate = new Date(now);

        const CUTOFF_HOUR = 12;
        const CUTOFF_MINUTE = 0;

        // 2. Check Cutoff First
        const cutoff = new Date(now);
        cutoff.setHours(CUTOFF_HOUR, CUTOFF_MINUTE, 0, 0);

        if (now > cutoff) {
            // Next day
            baseDate.setDate(baseDate.getDate() + 1);
        }

        // 1. Is it a business day? If not, find the next business day
        while (!this.isBusinessDay(baseDate)) {
            baseDate.setDate(baseDate.getDate() + 1);
        }

        // Reset to start of day for clean calculation
        baseDate.setHours(0, 0, 0, 0);
        return baseDate;
    }

    updateCountdown() {
        if (!this.els.rfCountdownText) return;

        const CUTOFF_HOUR = 12;
        const CUTOFF_MINUTE = 0;

        const now = new Date();
        let targetCutoff = new Date(now);
        targetCutoff.setHours(CUTOFF_HOUR, CUTOFF_MINUTE, 0, 0);

        let isTodayTarget = false;

        if (this.isBusinessDay(now) && now < targetCutoff) {
            isTodayTarget = true;
        } else {
            targetCutoff.setDate(targetCutoff.getDate() + 1);
            while (!this.isBusinessDay(targetCutoff)) {
                targetCutoff.setDate(targetCutoff.getDate() + 1);
            }
            isTodayTarget = false;
        }

        const diff = targetCutoff - now;
        if (diff < 0) return;

        const h = Math.floor(diff / (1000 * 60 * 60));
        const m = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        const s = Math.floor((diff % (1000 * 60)) / 1000);

        const hStr = String(h).padStart(2, '0');
        const mStr = String(m).padStart(2, '0');
        const sStr = String(s).padStart(2, '0');

        const dateStr = isTodayTarget ? "本日" : `${targetCutoff.getMonth() + 1}/${targetCutoff.getDate()}`;

        this.els.rfCountdownText.innerHTML =
            `${this._rfSettings.timerPrefix} <strong style="font-family:'JetBrains Mono'; letter-spacing:1px; margin: 0 0.4em;">${hStr}:${mStr}:${sStr}</strong> ${this._rfSettings.timerSuffix} <span style="border-bottom:1px solid currentColor; margin-left: 0.2em;">${dateStr}受付</span>`;
    }

    handleProductPageUpdate() {
        // 1. Get current variant ID
        const form = this.closest('form') || document.querySelector('form[action*="/cart/add"]');
        if (!form) return;

        const variantInput = form.querySelector('[name="id"]');
        if (!variantInput) return;

        const qtyInput = form.querySelector('[name="quantity"]');
        const qty = qtyInput ? parseInt(qtyInput.value, 10) : 1;
        const variantId = variantInput.value;

        // 2. Calculate Points
        const variantPoints = this._variantMap[variantId] || 0;

        // [FIX] Per-variant business days from variant metafield custom.delivery_rule_ref
        this._currentVariantDays = parseInt(this._daysMap[variantId], 10) || 0;

        // Total Points = (Variant Points * Qty) + Processing Points
        const totalPoints = (variantPoints + this._processingPoint) * qty;

        this.updateUI(totalPoints);
    }

    handleCartUpdate(cartData) {
        // TO BE IMPLEMENTED: Logic for Cart Drawer
        // Loop through items, find requiredDays for each, take MAX.
        // This is complex because we need points map for ALL items in cart.
        // For now, simpler implementation: 
        // We probably cannot calculate exact points for ALL items unless we have that data on the cart line item properties 
        // or a global JS map of all products (unlikely).
        // Strategy: We will render this component inside the cart drawer if possible with liquid data, 
        // OR fetch data. 
        // If this component is INSIDE a specific line item in the cart drawer, it behaves like product page (just that line).
        // If it is 'Cart Total Delivery', that's harder.

        // Scope of this task: "Cart Logic (Aggregation) ... max(All Items' requiredDays)"
        // This might require fetching checking cart items metaproperties or strictly relying on what we can render in Liquid.
        // I'll stick to Product Page logic primarily for now to ensure base functions.
    }

    generateMailto() {
        const productTitle = document.querySelector('h1')?.innerText || '商品';
        const qtyInput = document.querySelector('[name="quantity"]');
        const qty = qtyInput ? qtyInput.value : '不明';

        // Gather Options (Specific to custom-upload-form.liquid structure)
        // We look for checked radios or selected options inside the form
        const form = document.querySelector('#custom-upload-form') || this.closest('form');
        let optionsText = "";

        if (form) {
            const inputs = form.querySelectorAll('input:checked, select');
            inputs.forEach(input => {
                if (input.name && input.name !== 'id' && input.name !== 'quantity') {
                    // Try to find label logic or just use value
                    optionsText += `${input.name}: ${input.value}\n`;
                }
            });
        }

        const subject = encodeURIComponent(`【納期相談】${productTitle}について`);
        const body = encodeURIComponent(`以下の商品について納期の相談です。

商品名: ${productTitle}
数量: ${qty}
オプション:
${optionsText}

希望納期: 
備考: 
`);
        return `mailto:support@photopri.net?subject=${subject}&body=${body}`;
    }

    updateUI(totalPoints) {
        // Step 1: Calc Required Days
        let requiredDays;
        let isApprox = false;

        // [NEW] Feature: Check overrides based on Form Type or Zero Points
        const formType = (this.dataset.formType || '').trim();
        const productId = this.dataset.productId;
        const isSpecial5To7Days = (productId === '7207878197440' || productId === '7207808925888');

        // [NEW] Hide lead time for Student Discount
        if (formType === 'student_discount') {
            const statusEl = this.querySelector('.rf-dashboard-status');
            if (statusEl) statusEl.style.display = 'none';
            return;
        }

        // [FIX] Specific products 5-7 days display
        if (isSpecial5To7Days) {
            requiredDays = 7; // Use 7 for max estimation
            isApprox = true;
        }
        // 1. Trial Print & Caption Panel (Fixed 5 days, except specific variants)
        else if (formType === 'otameshi_print' || formType === 'caption_panel') {
            requiredDays = 5;
            // [FIX] Specific variant override: 全用紙セット → 7 business days
            if (formType === 'otameshi_print') {
                let isAllPaperSet = false;

                // Strategy 1: Check #rf-size-select text
                const sizeSelect = document.querySelector('#rf-size-select');
                if (sizeSelect && sizeSelect.selectedIndex >= 0) {
                    const selectedOpt = sizeSelect.options[sizeSelect.selectedIndex];
                    const selectedText = selectedOpt ? selectedOpt.textContent : '';
                    if (selectedText.includes('全用紙セット')) {
                        isAllPaperSet = true;
                    }
                    console.log('LeadTime: otameshi_print selected text:', selectedText.trim());
                }

                // Strategy 2: Fallback to variant ID
                if (!isAllPaperSet) {
                    const form = this.closest('form') || document.querySelector('form[action*="/cart/add"]');
                    let variantInput = form ? form.querySelector('[name="id"]') : null;
                    if (!variantInput) {
                        variantInput = document.querySelector('select[name="id"], input[name="id"]');
                    }
                    const currentVariantId = variantInput ? variantInput.value : null;
                    if (currentVariantId === '41683292848320') {
                        isAllPaperSet = true;
                    }
                }

                if (isAllPaperSet) {
                    requiredDays = 7;
                }
            }
        }
        // [PRIORITY] Temporary Print Only / Photo Card Quantity Logic
        // These run BEFORE totalPoints fallback so they apply even when points = 0
        else if (formType === 'print_only') {
            const form = this.closest('form') || document.querySelector('form[action*="/cart/add"]');
            const qtyInput = form ? form.querySelector('[name="quantity"]') : null;
            const qty = qtyInput ? parseInt(qtyInput.value, 10) : 1;

            requiredDays = 3; // Basic unified processing time

            let sizeW = 0, sizeH = 0;
            const sizeSelect = document.querySelector('#rf-size-select');
            const customShort = document.querySelector('#rf-size-short');
            const customLong = document.querySelector('#rf-size-long');
            const isCustomMode = document.querySelector('input[name="rf-size-mode"][value="custom"]')?.checked;
            
            if (isCustomMode && customShort && customLong) {
                sizeW = parseInt(customShort.value) || 0;
                sizeH = parseInt(customLong.value) || 0;
            } else if (sizeSelect && sizeSelect.value) {
                const sv = sizeSelect.value;
                const match = sv.match(/\(\s*(\d+)\s*[×xX]\s*(\d+)\s*\)/);
                if (match) {
                    sizeW = parseInt(match[1], 10);
                    sizeH = parseInt(match[2], 10);
                } else {
                    const dict = window.STANDARD_SIZE_DICT || {
                        'A5': [148, 210], 'A4': [210, 297], 'A3': [297, 420], 'A3ノビ': [329, 483], 'A2': [420, 594], 'A2ノビ': [432, 610],
                        'A1': [594, 841], 'A1ノビ': [610, 914], 'A0': [841, 1189], 'A0ノビ': [914, 1292],
                        'B5': [182, 257], 'B4': [257, 364], 'B3': [364, 515], 'B2': [515, 728], 'B1': [728, 1030], 'B1ノビ': [790, 1118],
                        'B0': [1030, 1456], 'B0ノビ': [1118, 1580],
                        'L': [89, 127], 'ハガキ': [100, 148], '2L': [128, 178], '六切': [203, 254], '四切': [254, 305], 'ワイド四切': [254, 365],
                        '半切': [356, 432], '全紙': [457, 559], '大全紙': [508, 610], '全倍': [600, 900],
                        'SM': [158, 227], 'F4': [242, 333], 'F6': [318, 410], 'F8': [380, 455], 'F10': [455, 530], 'F15': [530, 652], 'F20': [606, 727],
                        '200×200': [200, 200], '300×300': [300, 300], '400×400': [400, 400], '500×500': [500, 500], '600×600': [600, 600],
                        '800×800': [800, 800], '1000×1000': [1000, 1000], '1000×1500': [1000, 1500], '1000×2000': [1000, 2000], '1000×3000': [1000, 3000]
                    };
                    const keys = Object.keys(dict).sort((a,b) => b.length - a.length);
                    for (const k of keys) {
                        if (sv.startsWith(k)) {
                            sizeW = dict[k][0]; sizeH = dict[k][1]; break;
                        }
                    }
                }
            }

            if (sizeW > 0 && sizeH > 0) {
                const s = Math.min(sizeW, sizeH);
                const l = Math.max(sizeW, sizeH);
                const isUnderA2 = (s < 420 && l < 594); // A2未満(A2より小さいサイズ)
                
                if (!isUnderA2) {
                    // A2以上
                    if (qty >= 10) { requiredDays = Math.max(requiredDays, 999); }
                    else if (qty >= 7) { requiredDays = Math.max(requiredDays, 5); }
                } else {
                    // A2未満
                    if (qty >= 20) { requiredDays = Math.max(requiredDays, 999); }
                    else if (qty >= 10) { requiredDays = Math.max(requiredDays, 5); }
                }
            } else {
                if (qty >= 20) { requiredDays = Math.max(requiredDays, 999); }
                else if (qty >= 10) { requiredDays = Math.max(requiredDays, 5); }
            }
        }
        else if (formType === 'photo_card') {
            let handledByTempLogic = false;
            const form = this.closest('form') || document.querySelector('form[action*="/cart/add"]');
            const qtyInput = form ? form.querySelector('[name="quantity"]') : null;
            const qty = qtyInput ? parseInt(qtyInput.value, 10) : 1;

            if (qty >= 7) { requiredDays = 999; handledByTempLogic = true; }
            else if (qty >= 3) { requiredDays = 5; handledByTempLogic = true; }

            if (!handledByTempLogic) {
                if (totalPoints <= 0) {
                    requiredDays = 14;
                    isApprox = true;
                } else if (totalPoints > this._consultLimit) {
                    requiredDays = 999;
                } else if (totalPoints <= this._limitT1) {
                    requiredDays = Math.ceil((totalPoints / this._capT1) + 1);
                } else {
                    const baseDays = Math.ceil((this._limitT1 / this._capT1) + 1);
                    const excess = totalPoints - this._limitT1;
                    const extraDays = Math.ceil(excess / this._capT2);
                    requiredDays = baseDays + extraDays;
                }
            }
        }
        // 2. [FIX] Per-variant business days from variant metafield custom.delivery_rule_ref
        //    (single_line_text integer e.g. "10"/"3"). This is the primary path for Qoo.
        else if (this._currentVariantDays > 0) {
            requiredDays = this._currentVariantDays;
        }
        // 3. Legacy capacity-gated path (dormant: text metafield → points 0)
        else if (totalPoints > 0) {
            requiredDays = this._step2DaysOverride;
        }
        // 4. Metafield empty / unset → approximate 14 business days fallback
        else {
            requiredDays = 14;
            isApprox = true;
        }

        // [NEW] Cart Override Logic for Temporary Rules (Cross-Item Aggregation)
        if (this.dataset.cartItems) {
            try {
                const items = JSON.parse(this.dataset.cartItems);
                
                let totalPrintOnlyA2AndOver = 0;
                let totalPrintOnlyUnderA2 = 0;
                let totalPrintOnly = 0;
                let totalPhotoCard = 0;

                let maxIndividualDays = 0;
                let hasUnconfiguredItem = false;
                let sumPointsForCalculation = 0;

                for (const item of items) {
                    const itemFormType = (item.formType || '').trim();
                    const qty = parseInt(item.quantity || 0, 10);
                    const itemPoints = parseInt(item.points || 0, 10);
                    const hasDeliveryRule = item.hasDeliveryRule === true;
                    const productId = item.productId ? item.productId.toString() : '';
                    const isSpecial5To7Days = (productId === '7207878197440' || productId === '7207808925888');
                    const props = item.properties || {};

                    let tempDays = 0;
                    let handledByTemp = false;

                    if (itemFormType === 'student_discount') {
                        continue;
                    }

                    if (isSpecial5To7Days) {
                        tempDays = 7;
                        handledByTemp = true;
                    } else if (itemFormType === 'otameshi_print' || itemFormType === 'caption_panel') {
                        tempDays = 5;
                        handledByTemp = true;
                        if (itemFormType === 'otameshi_print') {
                            let isAllPaperSet = false;
                            for (const key in props) {
                                if (props[key].includes('全用紙セット') || key.includes('全用紙セット')) isAllPaperSet = true;
                            }
                            if (isAllPaperSet) tempDays = 7;
                        }
                    } else if (itemFormType === 'print_only') {
                        handledByTemp = true;
                        tempDays = 3; // Basic unified processing time

                        totalPrintOnly += qty;

                        let sizeW = 0, sizeH = 0;
                        const sizeStr = props['サイズ'] || '';
                        const shortStr = props['短辺(mm)'] || '';
                        const longStr = props['長辺(mm)'] || '';

                        if (shortStr && longStr) {
                            sizeW = parseInt(shortStr, 10) || 0;
                            sizeH = parseInt(longStr, 10) || 0;
                        } else if (sizeStr) {
                            const match = sizeStr.match(/\(\s*(\d+)\s*[×xX]\s*(\d+)\s*\)/);
                            if (match) {
                                sizeW = parseInt(match[1], 10);
                                sizeH = parseInt(match[2], 10);
                            } else {
                                const dict = window.STANDARD_SIZE_DICT || {
                                    'A5': [148, 210], 'A4': [210, 297], 'A3': [297, 420], 'A3ノビ': [329, 483], 'A2': [420, 594], 'A2ノビ': [432, 610],
                                    'A1': [594, 841], 'A1ノビ': [610, 914], 'A0': [841, 1189], 'A0ノビ': [914, 1292],
                                    'B5': [182, 257], 'B4': [257, 364], 'B3': [364, 515], 'B2': [515, 728], 'B1': [728, 1030], 'B1ノビ': [790, 1118],
                                    'B0': [1030, 1456], 'B0ノビ': [1118, 1580],
                                    'L': [89, 127], 'ハガキ': [100, 148], '2L': [128, 178], '六切': [203, 254], '四切': [254, 305], 'ワイド四切': [254, 365],
                                    '半切': [356, 432], '全紙': [457, 559], '大全紙': [508, 610], '全倍': [600, 900],
                                    'SM': [158, 227], 'F4': [242, 333], 'F6': [318, 410], 'F8': [380, 455], 'F10': [455, 530], 'F15': [530, 652], 'F20': [606, 727],
                                    '200×200': [200, 200], '300×300': [300, 300], '400×400': [400, 400], '500×500': [500, 500], '600×600': [600, 600],
                                    '800×800': [800, 800], '1000×1000': [1000, 1000], '1000×1500': [1000, 1500], '1000×2000': [1000, 2000], '1000×3000': [1000, 3000]
                                };
                                const keys = Object.keys(dict).sort((a,b) => b.length - a.length);
                                for (const k of keys) {
                                    if (sizeStr.startsWith(k)) {
                                        sizeW = dict[k][0]; sizeH = dict[k][1]; break;
                                    }
                                }
                            }
                        }

                        if (sizeW > 0 && sizeH > 0) {
                            const s = Math.min(sizeW, sizeH);
                            const l = Math.max(sizeW, sizeH);
                            const isUnderA2 = (s < 420 && l < 594);
                            
                            if (!isUnderA2) {
                                totalPrintOnlyA2AndOver += qty;
                            } else {
                                totalPrintOnlyUnderA2 += qty;
                            }
                        }
                    } else if (itemFormType === 'photo_card') {
                        totalPhotoCard += qty;
                        if (qty >= 7) { tempDays = 999; handledByTemp = true; }
                        else if (qty >= 3) { tempDays = 5; handledByTemp = true; }
                    } else if (itemPoints <= 0) {
                        tempDays = 14;
                        handledByTemp = true;
                    }

                    if (!handledByTemp) {
                        sumPointsForCalculation += itemPoints; // Accumulate points ONLY for non-temp items
                        if (itemPoints > this._consultLimit) tempDays = 999;
                        else if (itemPoints <= this._limitT1) tempDays = Math.ceil((itemPoints / this._capT1) + 1);
                        else {
                            const baseDays = Math.ceil((this._limitT1 / this._capT1) + 1);
                            const excess = itemPoints - this._limitT1;
                            const extraDays = Math.ceil(excess / this._capT2);
                            tempDays = baseDays + extraDays;
                        }
                    }

                    maxIndividualDays = Math.max(maxIndividualDays, tempDays);

                    if (!hasDeliveryRule && !handledByTemp && itemFormType !== 'photo_card') {
                        hasUnconfiguredItem = true;
                    }
                } // end loop

                // Re-calculate the overall requiredDays using the purely isolated points
                requiredDays = 0;
                if (sumPointsForCalculation > 0) {
                    if (sumPointsForCalculation > this._consultLimit) requiredDays = 999;
                    else if (sumPointsForCalculation <= this._limitT1) requiredDays = Math.ceil((sumPointsForCalculation / this._capT1) + 1);
                    else {
                        const baseDays = Math.ceil((this._limitT1 / this._capT1) + 1);
                        const excess = sumPointsForCalculation - this._limitT1;
                        const extraDays = Math.ceil(excess / this._capT2);
                        requiredDays = baseDays + extraDays;
                    }
                }

                // Update requiredDays to be at least maxIndividualDays
                if (maxIndividualDays > requiredDays) {
                    requiredDays = maxIndividualDays;
                }

                let cartMaxTempDays = 0;
                if (totalPrintOnly >= 20) cartMaxTempDays = Math.max(cartMaxTempDays, 999);
                else if (totalPrintOnly >= 10) cartMaxTempDays = Math.max(cartMaxTempDays, 5);
                if (totalPrintOnlyA2AndOver >= 10) cartMaxTempDays = Math.max(cartMaxTempDays, 999);
                else if (totalPrintOnlyA2AndOver >= 7) cartMaxTempDays = Math.max(cartMaxTempDays, 5);
                if (totalPrintOnlyUnderA2 >= 20) cartMaxTempDays = Math.max(cartMaxTempDays, 999);
                else if (totalPrintOnlyUnderA2 >= 10) cartMaxTempDays = Math.max(cartMaxTempDays, 5);
                if (totalPhotoCard >= 7) cartMaxTempDays = Math.max(cartMaxTempDays, 999);
                else if (totalPhotoCard >= 3) cartMaxTempDays = Math.max(cartMaxTempDays, 5);

                if (cartMaxTempDays > requiredDays) {
                    requiredDays = cartMaxTempDays;
                }

                if (hasUnconfiguredItem) {
                    if (requiredDays < 14) {
                        requiredDays = 14;
                        isApprox = true;
                    }
                }
            } catch (e) {
                console.error("LeadTimeCalculator: Failed to parse cart items JSON", e);
            }
        }

        // Min 3 days enforce
        if (requiredDays < 3) requiredDays = 3;

        // [NEW] Layout Confirmation +1 Day
        let wantsLayoutConfirm = false;

        if (this.dataset.cartItems) {
            // Cart mode
            try {
                const items = JSON.parse(this.dataset.cartItems);
                for (const item of items) {
                    if (item.properties && item.properties['レイアウト確認'] === '希望する') {
                        wantsLayoutConfirm = true;
                        break;
                    }
                }
            } catch(e) {}
        } else {
            // Product page mode
            const form = this.closest('form') || document.querySelector('form[action*="/cart/add"]');
            if (form) {
                const confirmCheck = form.querySelector('input[name="properties[レイアウト確認]"]:checked');
                if (confirmCheck && confirmCheck.value === '希望する') {
                    wantsLayoutConfirm = true;
                }
            }
        }

        if (wantsLayoutConfirm && requiredDays < 999) {
            requiredDays += 1;
        }

        // Status Logic
        let status = 'normal';
        let displayDays = `${requiredDays}営業日出荷`;
        if (isApprox) {
            displayDays = `約${requiredDays}営業日出荷`;
        }

        if (isSpecial5To7Days) {
            displayDays = '約5〜7営業日出荷';
        }
        let showPrefix = true;
        let isLink = false;

        if (requiredDays >= 30) {
            status = 'consult';
            // [FIX] カートコンテキストでは「お問い合わせ」ではなく「ご注文確定後に納期をお知らせ」
            const isCartMode = !!this.dataset.overridePoints;
            if (isCartMode) {
                displayDays = 'ご注文確定後に納期をお知らせ';
                showPrefix = false;
                isLink = false;
            } else {
                displayDays = 'お問い合わせください';
                showPrefix = false;
                isLink = true;
            }
        } else if (requiredDays >= 10) {
            status = 'alert';
        }

        // Render
        this.dataset.status = status;

        // Toggle Prefix
        if (this.els.prefix) {
            this.els.prefix.style.display = showPrefix ? 'inline' : 'none';
        }

        if (this.els.days) {
            if (isLink) {
                // Create Link if not exists, or update href
                const mailto = this.generateMailto();
                this.els.days.innerHTML = `<a href="${mailto}" style="color:inherit; text-decoration:underline;">${displayDays}</a>`;
            } else {
                // Reset to text
                this.animateValue(this.els.days, displayDays);
            }
        }

        // --- Generic Date Calculation Feature ---
        if (this.els.date) {
            if (status === 'consult') {
                const isCartMode = !!this.dataset.overridePoints;
                this.els.date.innerText = isCartMode ? '--' : '要相談';
            } else {
                const receptionDate = this.getReceptionDate();
                const shipDate = this.addBusinessDays(receptionDate, requiredDays);

                // Delivery is Ship + MIN to Ship + MAX
                const deliveryStart = new Date(shipDate);
                deliveryStart.setDate(deliveryStart.getDate() + this._step3MinOverride);

                const deliveryEnd = new Date(shipDate);
                deliveryEnd.setDate(deliveryEnd.getDate() + this._step3MaxOverride);

                const dateText = `${this.getFormattedDate(deliveryStart)} - ${this.getFormattedDate(deliveryEnd)}`;
                this.animateValue(this.els.date, dateText);
            }
        }

        // --- RF Timer Integration ---
        if (this.els.rfCountdownText && !this._timerInit) {
            this._timerInit = true;
            this.updateCountdown();
            setInterval(() => this.updateCountdown(), 1000);
        }

        // --- RF Timeline Calculation Feature ---
        if (this.els.rfDateReception && this.els.rfDateShipping && this.els.rfDateDelivery) {
            if (status === 'consult') {
                if (this.els.rfDateDesign) this.els.rfDateDesign.innerHTML = '<span style="font-size:0.8em">要相談</span>';
                this.els.rfDateShipping.innerHTML = '<span style="font-size:0.8em">要相談</span>';
                this.els.rfDateDelivery.innerHTML = '<span style="font-size:0.8em">要相談</span>';
            } else {
                const receptionDate = this.getReceptionDate();
                this.els.rfDateReception.innerText = this.getFormattedDate(receptionDate).split(' ')[0]; // only MM/DD

                // Calculate Design Date Range (from schema settings)
                const designMin = this._designDaysMin;
                const designMax = this._designDaysMax;
                const designDateStart = this.addBusinessDays(receptionDate, designMin);
                const designDateEnd = this.addBusinessDays(receptionDate, designMax);
                if (this.els.rfDateDesign) {
                    if (designMin === designMax) {
                        // Single date
                        this.els.rfDateDesign.innerText = this.getFormattedDate(designDateStart).split(' ')[0];
                    } else {
                        // Date range
                        this.els.rfDateDesign.innerText = `${this.getFormattedDate(designDateStart).split(' ')[0]}–${this.getFormattedDate(designDateEnd).split(' ')[0]}`;
                    }
                }

                // [FIX ②] Count requiredDays business days from the LATE end of the
                // design-confirm range (designDateEnd), not from reception. The late
                // confirm date itself counts as business day 1 (same convention as addBusinessDays).
                const shipDate = this.addBusinessDays(designDateEnd, requiredDays);
                this.els.rfDateShipping.innerText = this.getFormattedDate(shipDate).split(' ')[0];

                const deliveryStart = new Date(shipDate);
                deliveryStart.setDate(deliveryStart.getDate() + this._step3MinOverride);
                const deliveryEnd = new Date(shipDate);
                deliveryEnd.setDate(deliveryEnd.getDate() + this._step3MaxOverride);

                this.els.rfDateDelivery.innerText = `${this.getFormattedDate(deliveryStart).split(' ')[0]}–${this.getFormattedDate(deliveryEnd).split(' ')[0]}`;

                // Animate Timeline Steps
                if (this.els.rfStepDesign && !this.els.rfStepDesign.classList.contains('active')) {
                    setTimeout(() => {
                        this.els.rfStepDesign.classList.add('active');
                        setTimeout(() => {
                            if (this.els.rfStepShipping) this.els.rfStepShipping.classList.add('active');
                            setTimeout(() => {
                                if (this.els.rfStepDelivery) this.els.rfStepDelivery.classList.add('active');
                            }, 400);
                        }, 400);
                    }, 300);
                }
            }
        }
    }

    animateValue(el, newValue) {
        if (el.innerText !== newValue) {
            el.classList.remove('updated');
            void el.offsetWidth; // Trigger reflow
            el.innerText = newValue;
            el.classList.add('updated');
        }
    }
}

if (!customElements.get('lead-time-calculator')) {
    customElements.define('lead-time-calculator', LeadTimeCalculator);
}
